import { HistoryOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { selectActiveFilters, setFilters } from "../redux/global-map.slice"

/**
 * "History" — per explicit product direction, this is NOT a filter-bar
 * pill (an earlier version was; see `GlobalMapFilterBar.tsx`'s own git
 * history) but a standalone toggle beside `LayerToggleButtons`' Lead/Project
 * pills, styled to match them exactly per a later refinement: the same
 * `bg-sky-100` rounded-pill wrapper and the identical per-button "pressed =
 * white + shadow, unpressed = dimmed" treatment `LayerToggleButtons` itself
 * uses — a same-shape sibling rectangle, not a plain icon-only circle (the
 * first version of this component). `HistoryOutlined` (a clock face with a
 * counter-clockwise arrow) is Ant Design's own built-in icon for exactly
 * this concept — imported directly from `@ant-design/icons` rather than
 * through this app's custom `Icon` wrapper, which has no registered
 * clock/history glyph; several other call sites in this codebase already
 * import individual `@ant-design/icons` icons the same direct way.
 *
 * Still writes the exact same `GlobalMapFilters.includeHistory` field the
 * backend/URL-sync/Clear-All machinery already understands — only the
 * visual home and shape changed, not the underlying state (see
 * `active-filter-summary.ts`'s own `NON_CHIP_KEYS` comment for why it still
 * lives inside `GlobalMapFilters` rather than becoming a third top-level
 * field like `activeLayers`).
 *
 * The tooltip's verb flips with the CURRENT state — "Enable..." while off,
 * "Disable..." once on — describing what pressing the button right now
 * would do, not a static label.
 */
export function HistoryToggleButton() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const enabled = Boolean(filters.includeHistory)

  const toggle = () => {
    dispatch(setFilters({ includeHistory: !enabled }))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Spacebar"
    ) {
      event.preventDefault()
      toggle()
    }
  }

  const tooltip = t(
    enabled
      ? "globalMap.filters.disableHistoryTooltip"
      : "globalMap.filters.enableHistoryTooltip"
  )
  const label = t("globalMap.filters.history")

  return (
    <div
      role="group"
      aria-label={label}
      className="flex h-9 shrink-0 items-center gap-1 rounded-full bg-sky-100 px-1"
    >
      <Tooltip title={tooltip}>
        <button
          type="button"
          aria-pressed={enabled}
          onClick={toggle}
          onKeyDown={handleKeyDown}
          data-testid="global-map-history-toggle"
          className={cn(
            "flex h-7 cursor-pointer items-center gap-1.5 rounded-full pl-2 pr-3 text-xs font-medium transition-colors",
            enabled
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 opacity-70 hover:opacity-100"
          )}
        >
          <HistoryOutlined />
          {label}
        </button>
      </Tooltip>
    </div>
  )
}

export default HistoryToggleButton
